/**
 * Normativa cargada.
 *
 * Esta pantalla es la que hace comprobable la regla de oro del proyecto:
 * **toda afirmación normativa se traza a una fila con su artículo**.
 *
 * Y enseña lo que casi ningún sistema del sector enseña: qué reglas tienen
 * su contenido operativo en una **norma UNE que no es de acceso libre**.
 * Ahí el sistema identifica la norma y la marca como pendiente en lugar de
 * reconstruirla con información no fiable.
 */

import { useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';
import { RegulationVersions } from './regulation-versions.js';

interface Row {
  id: string;
  code: string;
  article_ref: string;
  operation_name: string;
  periodicity_unit: string | null;
  periodicity_value: number | null;
  lifecycle_event: string | null;
  allowed_performer: string;
  source_kind: string;
  une_reference: string | null;
  regulation_version: string;
  regulation_status: string;
  has_checklist: boolean;
}

function periodicity(r: Row): string {
  if (r.lifecycle_event) return 'Según ciclo de vida';
  if (r.periodicity_value === null || r.periodicity_unit === null) return '—';
  const unit = r.periodicity_unit === 'MONTH' ? 'mes' : 'año';
  const plural = r.periodicity_value === 1 ? unit : `${unit}s`;
  return `cada ${r.periodicity_value} ${plural}`;
}

const COLUMNS: Column<Row>[] = [
  { id: 'code', label: 'Regla', render: (r) => r.code, sortable: true, width: '7rem' },
  { id: 'operationName', label: 'Operación', render: (r) => r.operation_name },
  {
    id: 'periodicity',
    label: 'Periodicidad',
    width: '12rem',
    render: (r) => periodicity(r),
    exportValue: (r) => periodicity(r),
  },
  {
    id: 'allowedPerformer',
    label: 'Quién puede hacerlo',
    width: '13rem',
    render: (r) =>
      r.allowed_performer === 'MAINTAINER_OR_HOLDER' ? (
        // Dato comercial del anexo II §3: lo que el titular puede hacer por
        // su cuenta y lo que exige empresa mantenedora habilitada.
        <span className="pill pill--info">Titular o mantenedora</span>
      ) : (
        <span className="pill pill--off">Sólo mantenedora</span>
      ),
    exportValue: (r) =>
      r.allowed_performer === 'MAINTAINER_OR_HOLDER' ? 'Titular o mantenedora' : 'Sólo mantenedora',
  },
  {
    id: 'sourceKind',
    label: 'Fuente',
    width: '14rem',
    render: (r) =>
      r.source_kind === 'UNE' ? (
        <span className="pill pill--warn">⚠ {r.une_reference ?? 'UNE pendiente'}</span>
      ) : (
        <span className="pill pill--ok">✓ {r.source_kind}</span>
      ),
    exportValue: (r) => (r.source_kind === 'UNE' ? (r.une_reference ?? 'UNE') : r.source_kind),
  },
  { id: 'articleRef', label: 'Artículo', render: (r) => r.article_ref, sortable: true },
  {
    id: 'hasChecklist',
    label: 'Checklist',
    width: '7rem',
    render: (r) =>
      r.has_checklist ? <span className="pill pill--ok">✓</span> : <span className="due--none">—</span>,
    exportValue: (r) => (r.has_checklist ? 'Sí' : 'No'),
  },
  { id: 'regulationVersion', label: 'Versión', render: (r) => r.regulation_version, width: '8rem' },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'all', label: 'Todas', param: {} },
  { id: 'une', label: 'Dependen de una UNE', param: { pendingUne: 'true' } },
  { id: 'holder', label: 'Puede hacerlas el titular', param: { holderAllowed: 'true' } },
] as const;

export function RegulationScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<Row>({
    path: '/regulation/rules',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Normativa</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'all' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {listing.total !== null && <span className="screen__count">{listing.total} reglas</span>}
      </header>

      {quick.id === 'une' && (
        <p className="screen__note">
          Su contenido operativo está en una norma UNE, que no es de acceso libre. El sistema la
          identifica y la marca como pendiente de adquisición <strong>en lugar de reconstruirla</strong>{' '}
          con información no fiable. Ver la decisión P-01.
        </p>
      )}

      <RegulationVersions />

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName="reglas-normativa"
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        emptyMessage="No hay reglas cargadas. ¿Se ha publicado una versión de la normativa?"
      />
    </section>
  );
}
